import { KAPLAYCtx } from "kaplay";
import { BACKGROUND_TERRAIN, CAMERA, TERRAIN } from "./constants";
import { generateSmoothTerrain } from "./terrain";
import type {
  BackgroundLayer,
  BackgroundTerrain,
  Camera,
  TerrainPoint,
} from "./types";

export function generateMountainLayer(
  canvasHeight: number,
  heightOffset: number,
): BackgroundLayer {
  const baselineY = canvasHeight * CAMERA.BASELINE_FACTOR + heightOffset;

  const controlPoints: TerrainPoint[] = [];

  let x = -500;
  for (let i = 0; i < TERRAIN.NUM_HILLS; i++) {
    const dx = 200 + Math.random() * 100;
    x += dx;

    const peakHeight = 80 + Math.random() * 60;
    const isValley = Math.random() < 0.3;
    const y = isValley ? baselineY + peakHeight * 0.2 : baselineY - peakHeight;

    controlPoints.push({ x, y });
  }

  return { points: generateSmoothTerrain(controlPoints) };
}

export function generateHillLayer(
  canvasHeight: number,
  heightOffset: number,
): BackgroundLayer {
  const baselineY = canvasHeight * CAMERA.BASELINE_FACTOR + heightOffset;

  const controlPoints: TerrainPoint[] = [];

  let x = -500;
  for (let i = 0; i < TERRAIN.NUM_HILLS; i++) {
    const dx = 160 + Math.random() * 80;
    x += dx;

    const heightVariation = 40 + Math.random() * 30;
    const y = baselineY + (Math.random() - 0.5) * heightVariation;

    controlPoints.push({ x, y });
  }

  return { points: generateSmoothTerrain(controlPoints) };
}

export function extendMountainTerrain(
  terrain: TerrainPoint[],
  canvasHeight: number,
): void {
  if (terrain.length < 2) {
    return;
  }

  const lastPoint = terrain[terrain.length - 1];
  const baselineY =
    canvasHeight * CAMERA.BASELINE_FACTOR +
    BACKGROUND_TERRAIN.FAR_LAYER.HEIGHT_OFFSET;

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const controlPoints: TerrainPoint[] = [lastPoint];
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  let x = lastPoint.x;

  for (let i = 0; i < TERRAIN.NUM_HILLS; i++) {
    const dx = 200 + Math.random() * 100;
    x += dx;

    const peakHeight = 80 + Math.random() * 60;
    const isValley = Math.random() < 0.3;
    const y = isValley ? baselineY + peakHeight * 0.2 : baselineY - peakHeight;

    controlPoints.push({ x, y });
  }

  const newTerrain = generateSmoothTerrain(controlPoints);
  terrain.push(...newTerrain.slice(1));
}

export function extendHillTerrain(
  terrain: TerrainPoint[],
  canvasHeight: number,
): void {
  if (terrain.length < 2) {
    return;
  }

  const lastPoint = terrain[terrain.length - 1];
  const baselineY =
    canvasHeight * CAMERA.BASELINE_FACTOR +
    BACKGROUND_TERRAIN.NEAR_LAYER.HEIGHT_OFFSET;

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const controlPoints: TerrainPoint[] = [lastPoint];
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  let x = lastPoint.x;

  for (let i = 0; i < TERRAIN.NUM_HILLS; i++) {
    const dx = 160 + Math.random() * 80;
    x += dx;

    const heightVariation = 40 + Math.random() * 30;
    const y = baselineY + (Math.random() - 0.5) * heightVariation;

    controlPoints.push({ x, y });
  }

  const newTerrain = generateSmoothTerrain(controlPoints);
  terrain.push(...newTerrain.slice(1));
}

export function generateBackgroundTerrain(
  canvasHeight: number,
): BackgroundTerrain {
  return {
    farLayer: generateMountainLayer(
      canvasHeight,
      BACKGROUND_TERRAIN.FAR_LAYER.HEIGHT_OFFSET,
    ),
    nearLayer: generateHillLayer(
      canvasHeight,
      BACKGROUND_TERRAIN.NEAR_LAYER.HEIGHT_OFFSET,
    ),
  };
}

function getVisibleTerrain(
  terrain: TerrainPoint[],
  viewLeft: number,
  viewRight: number,
): TerrainPoint[] {
  let startIndex = 0;
  let endIndex = terrain.length - 1;

  for (const [i, element] of terrain.entries()) {
    if (element.x >= viewLeft) {
      startIndex = Math.max(0, i - 1);
      break;
    }
  }

  for (let i = startIndex; i < terrain.length; i++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (terrain[i].x > viewRight) {
      endIndex = Math.min(terrain.length - 1, i + 1);
      break;
    }
  }

  return terrain.slice(startIndex, endIndex + 1);
}

function extendTerrainBoundaries(
  visibleTerrain: TerrainPoint[],
  viewLeft: number,
  viewRight: number,
  terrain: TerrainPoint[],
): TerrainPoint[] {
  const extended = [...visibleTerrain];

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (extended[0].x > viewLeft) {
    const getTerrainY = (x: number) => {
      if (terrain.length < 2) {
        return terrain[0]?.y ?? 0;
      }

      let i = 0;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      while (i < terrain.length - 1 && terrain[i + 1].x < x) {
        i++;
      }

      if (i >= terrain.length - 1) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        return terrain[terrain.length - 1].y;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (i === 0 && x < terrain[0].x) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        return terrain[0].y;
      }

      const p0 = terrain[i];
      const p1 = terrain[i + 1];

      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (Math.abs(p1.x - p0.x) < 0.001) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        return p0.y;
      }

      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const t = Math.max(0, Math.min(1, (x - p0.x) / (p1.x - p0.x)));
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return p0.y + t * (p1.y - p0.y);
    };

    extended.unshift({ x: viewLeft, y: getTerrainY(viewLeft) });
  }

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (extended[extended.length - 1].x < viewRight) {
    const getTerrainY = (x: number) => {
      if (terrain.length < 2) {
        return terrain[0]?.y ?? 0;
      }

      let i = 0;
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      while (i < terrain.length - 1 && terrain[i + 1].x < x) {
        i++;
      }

      if (i >= terrain.length - 1) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        return terrain[terrain.length - 1].y;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (i === 0 && x < terrain[0].x) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        return terrain[0].y;
      }

      const p0 = terrain[i];
      const p1 = terrain[i + 1];

      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (Math.abs(p1.x - p0.x) < 0.001) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        return p0.y;
      }

      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      const t = Math.max(0, Math.min(1, (x - p0.x) / (p1.x - p0.x)));
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      return p0.y + t * (p1.y - p0.y);
    };

    extended.push({ x: viewRight, y: getTerrainY(viewRight) });
  }

  return extended;
}

function renderTerrainPolygon(
  k: KAPLAYCtx,
  terrain: TerrainPoint[],
  cameraPos: { x: number; y: number },
  viewportHeight: number,
  color: readonly [number, number, number],
  opacity: number,
): void {
  const bottomY = cameraPos.y + viewportHeight / 2 + 200;

  for (let i = 0; i < terrain.length - 1; i++) {
    const p1 = terrain[i];
    const p2 = terrain[i + 1];

    const segmentPoly = [
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p1.x, y: bottomY },
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p1.x, y: p1.y },
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p2.x, y: p2.y },
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      { x: p2.x, y: bottomY },
    ];

    k.drawPolygon({
      pts: segmentPoly.map((p) => k.vec2(p.x, p.y)),
      color: k.rgb(...color),
      opacity,
    });
  }
}

export function renderBackgroundLayer(
  k: KAPLAYCtx,
  layer: BackgroundLayer,
  camera: Camera,
  parallaxSpeed: number,
  color: readonly [number, number, number],
  opacity: number,
): void {
  const currentCameraPos = k.getCamPos();
  const viewportWidth = k.width() / camera.scale;
  const viewportHeight = k.height() / camera.scale;

  const parallaxOffset = currentCameraPos.x * (1 - parallaxSpeed);

  const viewLeft =
    currentCameraPos.x - viewportWidth / 2 - 2000 - parallaxOffset;
  const viewRight =
    currentCameraPos.x + viewportWidth / 2 + 2000 - parallaxOffset;

  const visibleTerrain = getVisibleTerrain(layer.points, viewLeft, viewRight);
  if (visibleTerrain.length <= 1) {
    return;
  }

  const adjustedTerrain = visibleTerrain.map((point) => ({
    x: point.x + parallaxOffset,
    y: point.y,
  }));

  const extendedTerrain = extendTerrainBoundaries(
    adjustedTerrain,
    viewLeft + parallaxOffset,
    viewRight + parallaxOffset,
    adjustedTerrain,
  );

  renderTerrainPolygon(
    k,
    extendedTerrain,
    currentCameraPos,
    viewportHeight,
    color,
    opacity,
  );
}

export function renderBackgroundTerrain(
  k: KAPLAYCtx,
  backgroundTerrain: BackgroundTerrain,
  camera: Camera,
): void {
  renderBackgroundLayer(
    k,
    backgroundTerrain.farLayer,
    camera,
    BACKGROUND_TERRAIN.FAR_LAYER.PARALLAX_SPEED,
    BACKGROUND_TERRAIN.FAR_LAYER.COLOR,
    BACKGROUND_TERRAIN.FAR_LAYER.OPACITY,
  );

  renderBackgroundLayer(
    k,
    backgroundTerrain.nearLayer,
    camera,
    BACKGROUND_TERRAIN.NEAR_LAYER.PARALLAX_SPEED,
    BACKGROUND_TERRAIN.NEAR_LAYER.COLOR,
    BACKGROUND_TERRAIN.NEAR_LAYER.OPACITY,
  );
}
